import { useEffect, useState } from 'react';
import Reveal from '../../components/Reveal';
import HeroSlideshow from '../../components/HeroSlideshow';
import SafeImage from '../../components/SafeImage';
import './nyerere.css';
import {
  IMAGE_FALLBACK,
  NYERERE_HERO_SLIDES,
  NYERERE_PAGE_IMAGES,
} from '../../data/destinationImages';

import lionFace from '../../assets/images/lion-face.jpg';
import elephantFace from '../../assets/images/elephant-face.jpg';
import eagleFace from '../../assets/images/eagle-face.jpg';
import wildebeestFace from '../../assets/images/wildebeest-face.jpg';
import wilddogFace from '../../assets/images/wilddog-face.jpg';
import rhinoFace from '../../assets/images/rhino-face.jpg';

const NYERERE_IMAGES = NYERERE_PAGE_IMAGES;

const HERO_STATS = [
  { value: '30,893', label: 'km² — Africa\'s largest', delay: 0 },
  { value: 'Rufiji', label: 'River boat safaris', delay: 80 },
  { value: 'UNESCO', label: 'Selous heritage', delay: 160 },
  { value: '2019', label: 'Nyerere NP gazetted', delay: 240 },
];

const WILDLIFE = [
  {
    animalFace: wilddogFace,
    title: 'African Wild Dogs',
    text: 'Nyerere holds one of Africa\'s most important wild dog populations watch packs hunt and rest along remote woodland tracks.',
    facePosition: 'center 36%',
  },
  {
    animalFace: elephantFace,
    title: 'Elephant Herds',
    text: 'Thousands of elephants roam riverine forest and miombo woodland among the greatest concentrations on the continent.',
    facePosition: 'center 42%',
  },
  {
    animalFace: lionFace,
    title: 'Lions & Leopards',
    text: 'Predators patrol the Rufiji floodplain and sand rivers lions are commonly seen, with leopards in denser bush.',
    facePosition: 'center 38%',
  },
  {
    animalFace: wildebeestFace,
    title: 'Hippos & Crocodiles',
    text: 'The Rufiji River and its lakes teem with hippos and Nile crocodiles best observed from a boat at dawn or dusk.',
    facePosition: 'center 35%',
  },
  {
    animalFace: rhinoFace,
    title: 'Giraffes & Antelope',
    text: 'Maasai giraffes, sable, roan, and Lichtenstein\'s hartebeest inhabit open glades between vast miombo woodlands.',
    facePosition: 'center 40%',
  },
  {
    animalFace: eagleFace,
    title: 'River & Woodland Birds',
    text: 'Fish eagles, pel\'s fishing owls, and African skimmers line the Rufiji over 440 bird species recorded in the reserve.',
    facePosition: 'center 28%',
  },
];

const SEASONS = [
  {
    badge: 'peak',
    title: 'Dry Season',
    months: 'June – October',
    text: 'Wildlife concentrates along the Rufiji and sand rivers. Cool mornings, clear skies, and superb boat and walking safari conditions.',
  },
  {
    badge: 'green',
    title: 'Green Season',
    months: 'November – March',
    text: 'Lush miombo woodland, calving season, and peak bird activity fewer camps open but exceptional privacy and photography.',
  },
  {
    badge: 'shoulder',
    title: 'Shoulder Season',
    months: 'April – May',
    text: 'Long rains green the landscape and many lodges close. Fly-in access may be limited plan with your operator in advance.',
  },
];

const EXPERIENCES = [
  {
    image: NYERERE_IMAGES.river,
    title: 'Rufiji River Boat Safari',
    text: 'Drift past hippo pods, basking crocodiles, and drinking elephants the signature Selous experience unchanged under Nyerere.',
  },
  {
    image: NYERERE_IMAGES.walking,
    title: 'Guided Walking Safari',
    text: 'Track wildlife on foot with an armed ranger through riverine forest and open sand rivers intimate and unforgettable.',
  },
  {
    image: NYERERE_IMAGES.savanna,
    title: 'Fly Camping & Remote Drives',
    text: 'Sleep under stars in mobile camps and explore vast miombo wilderness far from any road true deep-Africa adventure.',
  },
];

const GALLERY_IMAGES = [
  { src: NYERERE_IMAGES.hero, caption: 'Rufiji River' },
  { src: NYERERE_IMAGES.wildlife, caption: 'Savanna Wildlife' },
  { src: NYERERE_IMAGES.river, caption: 'Boat Safari' },
  { src: NYERERE_IMAGES.walking, caption: 'Riverine Bush' },
  { src: NYERERE_IMAGES.overview, caption: 'Wildebeest Herds' },
  { src: NYERERE_IMAGES.savanna, caption: 'Southern Wilderness' },
];

const SAFARI_PACKAGES = [
  {
    image: NYERERE_IMAGES.wildlife,
    title: 'Nyerere Fly-In Escape',
    duration: '3–4 days',
    tag: 'Short safari',
    text: 'Fly into a riverside camp for game drives through miombo woodland and sand rivers quiet wilderness close to Dar or Zanzibar.',
    highlights: ['Park airstrip transfers', 'Game drives', 'Riverside lodge'],
  },
  {
    image: NYERERE_IMAGES.river,
    title: 'Rufiji Boat & Drive Safari',
    duration: '4–5 days',
    tag: 'River focus',
    text: 'Mix dawn boat safaris on the Rufiji with afternoon drives hippos, crocs, and elephants in one classic Selous-style itinerary.',
    highlights: ['Boat safaris', 'Vehicle game drives', 'Hippo lakes'],
  },
  {
    image: NYERERE_IMAGES.walking,
    title: 'Walking & Fly-Camp Adventure',
    duration: '5–6 days',
    tag: 'Active safari',
    text: 'Track wildlife on foot with an armed ranger and sleep under canvas the intimate southern alternative to crowded northern circuits.',
    highlights: ['Guided walks', 'Fly camping', 'Remote bush nights'],
  },
  {
    image: NYERERE_IMAGES.savanna,
    title: 'Southern Circuit Wild South',
    duration: '7–10 days',
    tag: 'Most popular',
    text: 'Combine Nyerere with Ruaha or Mikumi for Africa\'s last great unfenced wilderness boat, walk, and fly-in lodges across the south.',
    highlights: ['Multi-park fly-ins', 'Boat + walking', 'Wild dog country'],
  },
];

const GETTING_THERE_ROUTES = [
  'Fly from Dar es Salaam or Zanzibar to park airstrips (Mtemere, Matambwe, Siwandu) the most practical access',
  'Road transfer from Dar via Morogoro is possible but long (6–8+ hrs) best split with Mikumi en route',
  'Combine with Ruaha National Park or Mikumi for a full Southern Circuit fly-in safari',
  'Allow at least 3–4 nights boat safaris, walking, and game drives reward unhurried pacing',
];

function Lightbox({ image, onClose }) {
  if (!image) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="nyerere-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      onClick={handleBackdropClick}
    >
      <button
        className="nyerere-lightbox__close"
        aria-label="Close image viewer"
        onClick={onClose}
      >
        ✕
      </button>
      <img
        className="nyerere-lightbox__img"
        src={image.src}
        alt={image.caption}
      />
      <span className="nyerere-lightbox__caption">{image.caption}</span>
    </div>
  );
}

function Nyerere() {
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="nyerere">
      <section className="nyerere-hero" aria-labelledby="nyerere-hero-heading">
        <div className="nyerere-hero__media" aria-hidden="true">
          <HeroSlideshow classPrefix="nyerere-hero" slides={NYERERE_HERO_SLIDES} fallback={IMAGE_FALLBACK} />
          <div className="nyerere-hero__overlay" />
        </div>
        <div className="page-container nyerere-hero__container">
          <div className="nyerere-hero__content">
            <nav className="nyerere-hero__breadcrumb" aria-label="Breadcrumb">
              <a href="/">Home</a>
              <span className="nyerere-hero__breadcrumb-sep" aria-hidden="true">›</span>
              <a href="#safaris">Southern Circuit</a>
              <span className="nyerere-hero__breadcrumb-sep" aria-hidden="true">›</span>
              <span>Nyerere</span>
            </nav>
            <p className="nyerere-hero__eyebrow">Southern Safari Circuit</p>
            <h1 id="nyerere-hero-heading">
              Nyerere
              <span>National Park</span>
            </h1>
            <p className="nyerere-hero__lead">
              Formerly the Selous Game Reserve Africa&apos;s largest national park,
              where the Rufiji River, walking safaris, and wild dog packs define
              Tanzania&apos;s wildest southern frontier.
            </p>
            <div className="nyerere-hero__actions">
              <a className="nyerere-btn nyerere-btn--primary" href="#contact">
                Plan your Nyerere safari
              </a>
              <a className="nyerere-btn nyerere-btn--outline" href="#nyerere-gallery">
                View gallery
              </a>
            </div>
          </div>
        </div>
        <div className="nyerere-hero__stats">
          {HERO_STATS.map((stat) => (
            <div
              key={stat.label}
              className="nyerere-hero__stat"
              style={{ '--stat-delay': `${stat.delay}ms` }}
            >
              <span className="nyerere-hero__stat-value">{stat.value}</span>
              <span className="nyerere-hero__stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section
        id="nyerere-overview"
        className="nyerere-section nyerere-overview"
        aria-labelledby="overview-heading"
      >
        <div className="page-container">
          <div className="nyerere-overview__grid">
            <Reveal className="nyerere-overview__text">
              <p className="nyerere-section__eyebrow">About the park</p>
              <h2 id="overview-heading">The soul of the old Selous</h2>
              <p>
                Nyerere National Park was gazetted in 2019 from the northern sector
                of the Selous Game Reserve a UNESCO World Heritage Site and,
                at 30,893 km², the largest national park in Africa. Named for
                Tanzania&apos;s founding father Julius Nyerere, it preserves one of
                the last great unfenced wilderness areas on the continent.
              </p>
              <p>
                What sets Nyerere apart is water. The Rufiji River and its network
                of lakes and channels create a riverine ecosystem unlike anything
                on the Northern Circuit boat safaris, walking safaris, and
                fly-camping replace crowded game-drive circuits with deep,
                slow-paced exploration.
              </p>
              <ul className="nyerere-overview__details">
                <Reveal as="li" className="nyerere-overview__detail" delay={0}>
                  <span className="nyerere-overview__detail-label">Location</span>
                  <span className="nyerere-overview__detail-value">Southern Tanzania</span>
                </Reveal>
                <Reveal as="li" className="nyerere-overview__detail" delay={80}>
                  <span className="nyerere-overview__detail-label">Park size</span>
                  <span className="nyerere-overview__detail-value">30,893 km²</span>
                </Reveal>
                <Reveal as="li" className="nyerere-overview__detail" delay={160}>
                  <span className="nyerere-overview__detail-label">Former name</span>
                  <span className="nyerere-overview__detail-value">Selous Game Reserve</span>
                </Reveal>
                <Reveal as="li" className="nyerere-overview__detail" delay={240}>
                  <span className="nyerere-overview__detail-label">Best for</span>
                  <span className="nyerere-overview__detail-value">Boat &amp; walking safaris</span>
                </Reveal>
              </ul>
            </Reveal>
            <Reveal className="nyerere-overview__image" variant="right" delay={200}>
              <SafeImage
                className="nyerere-overview__img"
                src={NYERERE_IMAGES.overview}
                alt="Wildebeest herd crossing a river in Nyerere National Park"
              />
              <span className="nyerere-overview__image-accent" aria-hidden="true" />
            </Reveal>
          </div>
        </div>
      </section>

      <section
        id="nyerere-packages"
        className="nyerere-section nyerere-packages"
        aria-labelledby="packages-heading"
      >
        <div className="page-container">
          <Reveal className="nyerere-section__header">
            <p className="nyerere-section__eyebrow">Safari packages</p>
            <h2 id="packages-heading">Nyerere safari packages</h2>
            <p className="nyerere-section__intro">
              From a short fly-in escape to a full Southern Circuit journey
              each package is tailored to your dates, camp style, and pace.
            </p>
          </Reveal>
          <ul className="nyerere-packages__grid">
            {SAFARI_PACKAGES.map((pkg, index) => (
              <Reveal
                key={pkg.title}
                as="li"
                className="nyerere-packages__card"
                delay={index * 100}
              >
                <div className="nyerere-packages__card-image">
                  <SafeImage src={pkg.image} alt={pkg.title} />
                  <span className="nyerere-packages__duration">{pkg.duration}</span>
                </div>
                <div className="nyerere-packages__card-body">
                  <span className="nyerere-packages__tag">{pkg.tag}</span>
                  <h3>{pkg.title}</h3>
                  <p>{pkg.text}</p>
                  <ul className="nyerere-packages__highlights">
                    {pkg.highlights.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <a className="nyerere-packages__cta" href="#contact">
                    Enquire about this package →
                  </a>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="nyerere-wildlife"
        className="nyerere-section nyerere-wildlife"
        aria-labelledby="wildlife-heading"
      >
        <div className="page-container">
          <Reveal className="nyerere-section__header">
            <p className="nyerere-section__eyebrow">Iconic wildlife</p>
            <h2 id="wildlife-heading">River, woodland &amp; wild dogs</h2>
            <p className="nyerere-section__intro">
              From painted wolf packs to Rufiji hippos Nyerere&apos;s ecosystems
              support wildlife densities and species rarely seen together elsewhere.
            </p>
          </Reveal>
          <ul className="nyerere-wildlife__grid">
            {WILDLIFE.map((item, index) => (
              <Reveal
                key={item.title}
                as="li"
                className="nyerere-wildlife__card"
                delay={index * 100}
                variant="scale"
              >
                <div className="nyerere-wildlife__icon-frame">
                  <span className="nyerere-wildlife__icon-ring" aria-hidden="true" />
                  <span
                    className="nyerere-wildlife__icon"
                    style={{ '--wildlife-face-position': item.facePosition }}
                  >
                    <img src={item.animalFace} alt={item.title} />
                  </span>
                </div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="nyerere-gallery"
        className="nyerere-section nyerere-gallery"
        aria-labelledby="gallery-heading"
      >
        <div className="page-container">
          <Reveal className="nyerere-section__header">
            <p className="nyerere-section__eyebrow">Photo gallery</p>
            <h2 id="gallery-heading">Scenes from Nyerere</h2>
            <p className="nyerere-section__intro">
              From Rufiji sunsets to miombo wilderness the landscapes and wildlife
              of Tanzania&apos;s greatest southern park.
            </p>
          </Reveal>
          <div className="nyerere-gallery__grid">
            {GALLERY_IMAGES.map((img, index) => (
              <Reveal
                key={img.caption}
                className="nyerere-gallery__item"
                delay={index * 100}
                variant="scale"
                onClick={() => setLightboxImage(img)}
                role="button"
                tabIndex={0}
                aria-label={`View ${img.caption}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setLightboxImage(img);
                  }
                }}
              >
                <SafeImage src={img.src} alt={img.caption} />
                <span className="nyerere-gallery__caption">{img.caption}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {lightboxImage && (
        <Lightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
      )}

      <section
        id="nyerere-seasons"
        className="nyerere-section nyerere-seasons"
        aria-labelledby="seasons-heading"
      >
        <div className="page-container">
          <Reveal className="nyerere-section__header">
            <p className="nyerere-section__eyebrow">Plan your timing</p>
            <h2 id="seasons-heading">Best time to visit Nyerere</h2>
            <p className="nyerere-section__intro">
              The dry season opens the full range of boat and walking safaris but
              each month brings its own rhythm along the Rufiji.
            </p>
          </Reveal>
          <ul className="nyerere-seasons__grid">
            {SEASONS.map((season, index) => (
              <Reveal
                key={season.title}
                as="li"
                className="nyerere-seasons__card"
                delay={index * 120}
              >
                <span
                  className={`nyerere-seasons__card-badge nyerere-seasons__card-badge--${season.badge}`}
                >
                  {season.badge === 'peak'
                    ? '★ Peak Season'
                    : season.badge === 'green'
                      ? '🌿 Green Season'
                      : '◐ Shoulder Season'}
                </span>
                <h3>{season.title}</h3>
                <span className="nyerere-seasons__card-months">{season.months}</span>
                <p>{season.text}</p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="nyerere-experiences"
        className="nyerere-section nyerere-experiences"
        aria-labelledby="experiences-heading"
      >
        <div className="page-container">
          <Reveal className="nyerere-section__header">
            <p className="nyerere-section__eyebrow">Safari experiences</p>
            <h2 id="experiences-heading">Ways to explore Nyerere</h2>
            <p className="nyerere-section__intro">
              Boat, boot, and fly-camp Nyerere rewards travellers who slow down
              and let the Rufiji set the pace.
            </p>
          </Reveal>
          <ul className="nyerere-experiences__grid">
            {EXPERIENCES.map((exp, index) => (
              <Reveal
                key={exp.title}
                as="li"
                className="nyerere-experiences__card"
                delay={index * 120}
              >
                <div className="nyerere-experiences__card-image">
                  <SafeImage src={exp.image} alt={exp.title} />
                </div>
                <div className="nyerere-experiences__card-body">
                  <h3>{exp.title}</h3>
                  <p>{exp.text}</p>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="nyerere-getting-there"
        className="nyerere-section nyerere-getting-there"
        aria-labelledby="getting-there-heading"
      >
        <div className="page-container">
          <div className="nyerere-getting-there__inner">
            <Reveal className="nyerere-getting-there__copy">
              <p className="nyerere-section__eyebrow">Getting there</p>
              <h2 id="getting-there-heading">How to reach Nyerere National Park</h2>
              <p>
                Nyerere lies in southern Tanzania, roughly 220 km southwest of Dar
                es Salaam. Most visitors fly into one of several bush airstrips
                within the park road access is possible but demands a long day
                of driving.
              </p>
              <ul className="nyerere-getting-there__routes">
                {GETTING_THERE_ROUTES.map((route) => (
                  <li key={route}>{route}</li>
                ))}
              </ul>
            </Reveal>
            <Reveal className="nyerere-getting-there__map" variant="right" delay={200}>
              <iframe
                title="Nyerere National Park location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d510078!2d38.5!3d-7.8!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x185c8b3cb8d057%3A0x7f37e1f0b9b5f4c!2sNyerere%20National%20Park!5e0!3m2!1sen!2stz!4v1700000000000!5m2!1sen!2stz"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="nyerere-cta" aria-labelledby="nyerere-cta-heading">
        <div className="page-container">
          <Reveal variant="scale" delay={0}>
            <div className="nyerere-cta__inner">
              <h2 id="nyerere-cta-heading">
                Ready for the Rufiji wilderness?
              </h2>
              <p>
                Tell us your travel dates we&apos;ll design a tailor-made
                Nyerere safari with boat trips, walking safaris, and Southern
                Circuit connections.
              </p>
              <a
                className="nyerere-btn nyerere-btn--primary nyerere-btn--lg"
                href="#contact"
              >
                Get a free Nyerere quote
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

export default Nyerere;
